/*
--------------------------------------------------------------------------------
--------------------------------> CART (AJAX) <---------------------------------
--------------------------------------------------------------------------------

  A thin wrapper over Shopify's Cart AJAX API.

  Why this exists: the cart is the only place a customer's crop is stored
  server-side, so it is the only place we can restore it from when they come
  back to a product page on a different device, in a new session, or after
  clearing their browser. Reading it needs to be cheap and reliable.

  It also lets us UPDATE the line the customer came back to edit, rather than
  adding a second, near-identical line to the basket -- which is what happened
  before, and which is how people ended up checking out with the wrong crop.

  jQuery Deferreds, not native Promises: this theme runs jQuery 1.11 and there
  is no reason to raise the browser floor to fix a data bug.
*/

window.CR = window.CR || {};

CR.cart = (function ($) {
  'use strict';

  var ENDPOINTS = {
    get: '/cart.js',
    add: '/cart/add.js',
    change: '/cart/change.js'
  };

  var TIMEOUT_MS = 15000;
  var MAX_ATTEMPTS = 3;
  var RETRY_BASE_MS = 400;

  // Shopify rate limits the cart API and will occasionally 429 or 5xx under
  // load. Those are worth retrying; a 4xx that is not 429 is not.
  function isRetryable(status) {
    return status === 0 || status === 429 || (status >= 500 && status < 600);
  }

  function request(url, method, data, attempt) {
    attempt = attempt || 1;

    var deferred = $.Deferred();

    $.ajax({
      url: url,
      type: method,
      dataType: 'json',
      timeout: TIMEOUT_MS,
      contentType: method === 'POST' ? 'application/json' : undefined,
      data: method === 'POST' ? JSON.stringify(data) : data
    })
      .done(function (response) {
        deferred.resolve(response);
      })
      .fail(function (xhr, textStatus) {
        var status = xhr ? xhr.status : 0;

        if (isRetryable(status) && attempt < MAX_ATTEMPTS) {
          var wait = RETRY_BASE_MS * Math.pow(2, attempt - 1);
          window.setTimeout(function () {
            request(url, method, data, attempt + 1)
              .done(function (response) { deferred.resolve(response); })
              .fail(function (error) { deferred.reject(error); });
          }, wait);
          return;
        }

        deferred.reject(describeError(xhr, textStatus));
      });

    return deferred.promise();
  }

  // Shopify returns a JSON body with `description` / `message` on cart errors.
  // Surface that rather than a bare status code, so the customer-facing message
  // can say something useful.
  function describeError(xhr, textStatus) {
    var error = {
      status: xhr ? xhr.status : 0,
      textStatus: textStatus || 'error',
      message: ''
    };

    if (xhr && xhr.responseJSON) {
      error.message = xhr.responseJSON.description ||
                      xhr.responseJSON.message ||
                      '';
    }
    if (!error.message && error.textStatus === 'timeout') {
      error.message = 'The request timed out.';
    }
    return error;
  }

  function get() {
    return request(ENDPOINTS.get, 'GET', { _: new Date().getTime() });
  }

  // payload: { id: variantId, quantity: n, properties: {..} }
  function add(payload) {
    return request(ENDPOINTS.add, 'POST', payload);
  }

  // Change quantity and/or properties of an existing line.
  //
  // Note: Shopify derives the line key from variant + properties, so changing
  // properties gives the line a NEW key. Callers must re-read the key from the
  // returned cart rather than assuming it is stable.
  function changeLine(key, payload) {
    var body = $.extend({ id: key }, payload);
    return request(ENDPOINTS.change, 'POST', body);
  }

  function removeLine(key) {
    return request(ENDPOINTS.change, 'POST', { id: key, quantity: 0 });
  }

  // The variant of an existing line cannot be changed in place, so swapping
  // size/depth/edge means removing the old line and adding a new one. Done in
  // this order so a failure leaves the basket with the old line intact rather
  // than with nothing.
  function replaceLine(key, payload) {
    var deferred = $.Deferred();

    add(payload)
      .done(function () {
        removeLine(key)
          .done(function (cart) { deferred.resolve(cart); })
          .fail(function () {
            // The new line is in but the old one would not come out. Better to
            // report success and let the customer remove the duplicate than to
            // claim failure and have them add a third.
            get()
              .done(function (cart) { deferred.resolve(cart); })
              .fail(function (error) { deferred.reject(error); });
          });
      })
      .fail(function (error) {
        deferred.reject(error);
      });

    return deferred.promise();
  }

  // Find the line key for a line matching this variant and image, after a
  // change that will have rekeyed it.
  function findLineKey(cart, variantId, imageFilename) {
    if (!cart || !cart.items) { return ''; }

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (variantId && String(item.variant_id) !== String(variantId)) { continue; }

      if (!imageFilename) { return item.key || ''; }

      var props = item.properties || {};
      var filename = CR.state.filenameFromUrl(props[CR.state.PROP_THUMBNAIL]) ||
                     CR.state.filenameFromUrl(props[CR.state.PROP_ORIGINAL]);
      if (filename === imageFilename) { return item.key || ''; }
    }
    return '';
  }

  return {
    get: get,
    add: add,
    changeLine: changeLine,
    removeLine: removeLine,
    replaceLine: replaceLine,
    findLineKey: findLineKey
  };
})(jQuery);
